import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Text,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { stableVaultContractConfig } from "@config/contractsConfig";
import { CONNECT_WALLET } from "@constants/analytics";
import { CONNECT_WALLET_CTA, SWITCH_NETWORK } from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappChain from "@hooks/useDappChain";
import useDappConfig from "@hooks/useDappConfig";
import useStableVaultClaims from "@hooks/useStableVaultClaim";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { format } from "date-fns";
import { useCallback } from "react";
import { emitReduxEvent, formatNumber, trackEvent } from "utils";
import { formatUnits } from "viem";
import { useAccount } from "wagmi";

const StableClaim = () => {
  const { isConnected } = useAccount();
  const { isStableVaultSupportedChain } = useDappConfig();
  const { chain } = useDappChain();
  const { openConnectModal } = useConnectModal();

  const { claims, isLoading } = useStableVaultClaims();

  const handleConnectWallet = useCallback(() => {
    openConnectModal?.();
    trackEvent(CONNECT_WALLET, {
      type: "stable_vault_claim",
    });
  }, [openConnectModal]);

  const handleClaim = useCallback(
    (claimData: any) => {
      if (!isConnected) {
        handleConnectWallet();
        return;
      }

      if (!isStableVaultSupportedChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
        return;
      }

      if (!claimData.isClaimable) {
        return;
      }

      const tokenConfig =
        stableVaultContractConfig[chain.id]?.tokens?.[claimData.tokenSymbol];
      const decimals = tokenConfig?.decimals || 18;

      const trackingAmount = parseFloat(
        formatUnits(claimData.claimableAmount || 0n, decimals)
      );

      emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_REDEEM_ASSET, {
        shares: claimData.shares,
        amountOut: trackingAmount.toString(),
        outputToken: claimData.tokenSymbol,
        receiver: claimData.receiver,
        controller: claimData.receiver,
      });
    },
    [isConnected, isStableVaultSupportedChain, handleConnectWallet, chain.id]
  );

  const getButtonText = (claimData: any) => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else if (!isStableVaultSupportedChain) {
      return SWITCH_NETWORK;
    } else if (claimData.type === "pending") {
      return "Processing...";
    } else if (!claimData.isClaimable) {
      return "Not Available";
    } else {
      return "Claim";
    }
  };

  const isButtonDisabled = (claimData: any) => {
    if (!isConnected) return false;
    if (!isStableVaultSupportedChain) return false;
    return !claimData.isClaimable || claimData.type === "pending";
  };

  if (!isConnected) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        py="60px"
        px="20px"
      >
        <Text
          fontSize="14px"
          color="#A1B6BA"
          textAlign="center"
          mb="24px"
          lineHeight="20px"
        >
          Connect your wallet to view your pending claims
        </Text>
        <Button
          borderRadius="12px"
          bg="textPrimary"
          p="8px 16px"
          h="48px"
          color="backgroundCard"
          fontSize="16px"
          fontWeight="600"
          onClick={handleConnectWallet}
        >
          {CONNECT_WALLET_CTA}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex
      fontSize="14px"
      lineHeight="20px"
      fontWeight="400"
      pt="20px"
      direction="column"
      px="16px"
    >
      {/* Desktop Table View */}
      <Box display={{ base: "none", md: "block" }}>
        {/* Claims List */}
        <Box minH="150px">
          {isLoading ? (
            <Flex align="center" justify="center" h="300px">
              <VStack spacing="12px">
                <Spinner
                  thickness="3px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="#075a5a"
                  size="lg"
                />
                <Text fontSize="14px" color="#A1B6BA">
                  Loading claims...
                </Text>
              </VStack>
            </Flex>
          ) : !claims || claims.length === 0 ? (
            <Flex align="center" justify="center" h="150px">
              <Text
                fontSize="14px"
                color="#A1B6BA"
                textAlign="center"
                px="20px"
              >
                No pending or claimable withdrawals available
              </Text>
            </Flex>
          ) : (
            <>
              {/* Header - only show when there are claims */}
              <Flex
                mb="16px"
                px="16px"
                py="12px"
                bg="#F8F9FA"
                borderRadius="8px"
                fontWeight="600"
                fontSize="12px"
                color="#3F6870"
                letterSpacing="0.5px"
              >
                <Text flex="1">Request Date</Text>
                <Text flex="1" textAlign="center">
                  Amount
                </Text>
                <Text flex="1" textAlign="center">
                  Status
                </Text>
                <Text flex="1" textAlign="right">
                  Action
                </Text>
              </Flex>

              <Box maxH="400px" overflowY="auto">
                {claims.map((claim: any, index: number) => (
                  <Flex
                    key={claim.id || index}
                    align="center"
                    p="16px"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                    _hover={{ bg: "#F8F9FA" }}
                  >
                    {/* Request Date */}
                    <Flex flex="1" direction="column">
                      <Text fontSize="14px" fontWeight="500" color="#2D3748">
                        {format(
                          new Date(claim.requestTime || claim.claimableAt),
                          "dd MMM yyyy"
                        )}
                      </Text>
                      {/* <Text fontSize="12px" color="#A1B6BA">
                    {format(
                      new Date(claim.requestTime || claim.claimableAt),
                      "HH:mm"
                    )}
                  </Text> */}
                    </Flex>

                    {/* Amount */}
                    <Flex flex="1" direction="column" align="center">
                      <Text fontSize="14px" fontWeight="600" color="#2D3748">
                        {(() => {
                          try {
                            const amount = claim.claimableAmount || 0n;
                            const tokenConfig =
                              stableVaultContractConfig[chain.id]?.tokens?.[
                                claim.tokenSymbol
                              ];
                            const decimals = tokenConfig?.decimals || 18;
                            const formattedAmount = formatUnits(
                              amount,
                              decimals
                            );
                            const numericAmount = parseFloat(formattedAmount);
                            const displayAmount = formatNumber(
                              numericAmount,
                              false,
                              4,
                              true
                            );

                            return displayAmount;
                          } catch (_error) {
                            return "0";
                          }
                        })()}
                      </Text>
                      <Text fontSize="12px" color="#A1B6BA">
                        {claim.tokenSymbol}
                      </Text>
                    </Flex>

                    {/* Status */}
                    <Flex flex="1" direction="column" align="center">
                      <Text
                        fontSize="12px"
                        fontWeight="600"
                        color={claim.type === "pending" ? "#EAA935" : "#28A745"}
                        // textTransform="uppercase"
                        letterSpacing="0.5px"
                      >
                        {claim.type === "pending" ? "Pending" : "Ready"}
                      </Text>
                      {claim.type === "pending" && (
                        <Text fontSize="10px" color="#A1B6BA">
                          Available:{" "}
                          {format(new Date(claim.claimableAt), "dd MMM yyyy")}
                        </Text>
                      )}
                    </Flex>

                    {/* Action */}
                    <Flex flex="1" justify="flex-end">
                      <Button
                        size="sm"
                        borderRadius="8px"
                        bg={
                          claim.isClaimable &&
                          isConnected &&
                          isStableVaultSupportedChain &&
                          claim.type === "claimable"
                            ? "textPrimary"
                            : "gray.200"
                        }
                        color={
                          claim.isClaimable &&
                          isConnected &&
                          isStableVaultSupportedChain &&
                          claim.type === "claimable"
                            ? "backgroundCard"
                            : "gray.500"
                        }
                        fontSize="14px"
                        fontWeight="600"
                        px="20px"
                        py="8px"
                        h="36px"
                        isDisabled={isButtonDisabled(claim)}
                        onClick={() => handleClaim(claim)}
                        _hover={{
                          bg:
                            claim.isClaimable &&
                            isConnected &&
                            isStableVaultSupportedChain &&
                            claim.type === "claimable"
                              ? "textPrimary"
                              : "gray.200",
                        }}
                        _disabled={{
                          cursor: "not-allowed",
                          opacity: 0.6,
                        }}
                      >
                        {getButtonText(claim)}
                      </Button>
                    </Flex>
                  </Flex>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Mobile Card View */}
      <Box display={{ base: "block", md: "none" }}>
        <Box minH="150px">
          {isLoading ? (
            <Flex align="center" justify="center" h="300px">
              <VStack spacing="12px">
                <Spinner
                  thickness="3px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="#075a5a"
                  size="lg"
                />
                <Text fontSize="14px" color="#A1B6BA">
                  Loading claims...
                </Text>
              </VStack>
            </Flex>
          ) : !claims || claims.length === 0 ? (
            <Flex align="center" justify="center" h="150px">
              <Text
                fontSize="14px"
                color="#A1B6BA"
                textAlign="center"
                px="20px"
              >
                No pending or claimable withdrawals available
              </Text>
            </Flex>
          ) : (
            <VStack spacing="16px" align="stretch">
              {claims.map((claim: any, index: number) => (
                <Card
                  key={claim.id || index}
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="gray.200"
                  _hover={{ shadow: "md" }}
                  transition="all 0.2s"
                >
                  <CardBody p="16px">
                    <VStack spacing="16px" align="stretch">
                      {/* Header with Date and Status */}
                      <Flex justify="space-between" align="flex-start">
                        <VStack
                          align="flex-start"
                          justify="center"
                          alignItems="flex-start"
                          spacing="4px"
                        >
                          <Flex gap="4px">
                            <Text
                              fontSize="16px"
                              fontWeight="600"
                              color="#2D3748"
                            >
                              {(() => {
                                try {
                                  const amount = claim.claimableAmount || 0n;
                                  const tokenConfig =
                                    stableVaultContractConfig[chain.id]
                                      ?.tokens?.[claim.tokenSymbol];
                                  const decimals = tokenConfig?.decimals || 18;
                                  const formattedAmount = formatUnits(
                                    amount,
                                    decimals
                                  );
                                  const numericAmount =
                                    parseFloat(formattedAmount);
                                  const displayAmount = formatNumber(
                                    numericAmount,
                                    false,
                                    4,
                                    true
                                  );

                                  return displayAmount;
                                } catch (_error) {
                                  return "0";
                                }
                              })()}
                            </Text>
                            <Text fontSize="12px" color="#A1B6BA">
                              {claim.tokenSymbol}
                            </Text>
                          </Flex>
                        </VStack>
                        <Badge
                          fontSize="10px"
                          fontWeight="600"
                          color={
                            claim.type === "pending" ? "#EAA935" : "#28A745"
                          }
                          bg={claim.type === "pending" ? "#FEF5E7" : "#E8F5E8"}
                          // textTransform="uppercase"
                          letterSpacing="0.5px"
                          px="8px"
                          py="4px"
                          borderRadius="6px"
                        >
                          {claim.type === "pending" ? "Pending" : "Ready"}
                        </Badge>
                      </Flex>
                      {/* Amount and Token */}
                      <Flex justify="flex-start" align="flex-end" gap="4px">
                        <Text fontSize="12px" color="#2D3748">
                          <strong>Request Date:</strong>{" "}
                          {format(
                            new Date(claim.requestTime || claim.claimableAt),
                            "dd MMM yyyy"
                          )}
                        </Text>
                      </Flex>
                      {/* Available Date for Pending Claims */}
                      {claim.type === "pending" && (
                        <Box
                          p="12px"
                          bg="#F8F9FA"
                          borderRadius="8px"
                          borderLeft="3px solid"
                          borderColor="#EAA935"
                        >
                          <Text fontSize="12px" color="#A1B6BA">
                            <strong>Available:</strong>{" "}
                            {format(new Date(claim.claimableAt), "dd MMM yyyy")}
                          </Text>
                        </Box>
                      )}
                      {/* Action Button */}
                      <Button
                        size="md"
                        borderRadius="8px"
                        bg={
                          claim.isClaimable &&
                          isConnected &&
                          isStableVaultSupportedChain &&
                          claim.type === "claimable"
                            ? "textPrimary"
                            : "gray.200"
                        }
                        color={
                          claim.isClaimable &&
                          isConnected &&
                          isStableVaultSupportedChain &&
                          claim.type === "claimable"
                            ? "backgroundCard"
                            : "gray.500"
                        }
                        fontSize="14px"
                        fontWeight="600"
                        h="44px"
                        isDisabled={isButtonDisabled(claim)}
                        onClick={() => handleClaim(claim)}
                        _hover={{
                          bg:
                            claim.isClaimable &&
                            isConnected &&
                            isStableVaultSupportedChain &&
                            claim.type === "claimable"
                              ? "textPrimary"
                              : "gray.200",
                        }}
                        _disabled={{
                          cursor: "not-allowed",
                          opacity: 0.6,
                        }}
                      >
                        {getButtonText(claim)}
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}
        </Box>
      </Box>

      {/* Footer note */}
      <Box
        mt="20px"
        p="16px"
        bg="#F0F8FF"
        borderRadius="8px"
        borderLeft="4px solid"
        borderColor="#075a5a"
      >
        <Text fontSize="12px" color="#075a5a" lineHeight="16px">
          <strong>Note:</strong> Withdrawal requests go through a 3-day
          processing period. Pending withdrawals will appear here and become
          claimable once processed by the vault operator. You can only claim
          withdrawals that are marked as &quot;Ready&quot;.
        </Text>
      </Box>
    </Flex>
  );
};

export default StableClaim;
